import { Fragment, useEffect, useState } from 'react';
import { api, darf, datum, heuteIso, menge, zeitpunkt, type Anmelder } from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { MitGrund, NeuePosition } from './Aufmass.tsx';
import { Kopierbar } from '../teile/Kopierbar.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import {
  eingabenAus, feldAusMenge, listeMitZeile, listeOhneZeile, mengeAusFeld,
  nochNichtGespeichert, vorhabenAus, type Hoechstlaenge,
  type Aenderung, type Eingabe, type Zeile,
} from '../teile/lebmengen.ts';

// Der LEB-Export: die CSV fürs Telekom-Portal.
//
// Der Bildschirm zeigt VOR dem Erzeugen, was in der Datei stehen wird, und
// was dagegen spricht. Alles, was hier steht, kommt aus derselben Rechnung,
// aus der auch die Datei gebaut wird: Vorschau und Ergebnis können nicht
// auseinanderlaufen.
//
// Die Datei geht NICHT in den Auftragsordner. Dort haben die Subunternehmer
// Zugriff, und in der CSV stehen unsere Mengen.

const ARTEN = [
  { wert: 'komplett', text: 'Komplett (Schlussrechnung)' },
  { wert: 'teil', text: 'Teilaufmaß (Schlussmarke bleibt offen)' },
  { wert: 'schluss', text: 'Schlussaufmaß (Nachtrag)' },
];

/* IN DER LISTE DER FRÜHEREN AUSFUHREN STAND DER ROHE WERT („teil").
   Marvin, 01.09.2026, zur Behördenmappe: „kannst du hier bei der „Art" die
   umlaute richtig schreiben." Dasselbe gilt hier: der Wert bleibt ASCII,
   der Text daneben ist deutsch. Der Zusatz in Klammern gehört ins
   AUSWAHLFELD (dort entscheidet man) und nicht in eine Liste, in der man
   nachsieht, was schon hinausging. */
const ART_KURZ: Record<string, string> = {
  komplett: 'Komplett', teil: 'Teilaufmaß', schluss: 'Schlussaufmaß',
};

/**
 * Was der BILDSCHIRM ausserdem zeigt: die Gegenüberstellung.
 *
 * `Zeile` in `teile/lebmengen.ts` trägt nur, was die RECHNUNG braucht - was
 * getippt wurde, was gerechnet ist, ob es abweicht. Die gemeldete und die
 * geprüfte Menge stehen daneben, damit ein Mensch sieht, wovon er abweicht;
 * gerechnet wird mit keiner von beiden. Sie gehören deshalb hierher und
 * nicht in die Regeldatei: eine Regel, die Felder trägt, die sie nie liest,
 * lädt dazu ein, doch mit ihnen zu rechnen.
 */
type Anzeigezeile = Zeile & { mengeSub: number | null; mengeMds: number | null };


/**
 * DIE BEIDEN SYMBOLE AM ENDE EINER ZEILE.
 *
 * ORIGINALTEXT MARVIN (10.09.2026)
 *   „am ende jeder spalte machst du ein symbol um direkt dort live zu ändern
 *    und ein symbol um die spalte zu löschen, bzw. deaktiereven, so dass sie
 *    nicht in die csv kommt."
 *
 * SIE SIND GEZEICHNET UND NICHT GELADEN - dieselbe Entscheidung wie bei jedem
 * anderen Sinnbild dieses Hauses: keine Bibliothek für 2 Striche.
 *
 * UND JEDES TRÄGT SEINEN NAMEN (`aria-label` und `title`). Ein Sinnbild ohne
 * Namen ist auf dem Telefon stumm, und wer nicht weiss, was der Knopf tut,
 * drückt ihn nicht - oder einmal zu oft. Der ausgeschriebene Weg zu denselben
 * Zahlen bleibt daneben stehen: „Mengen ändern" öffnet die ganze Liste.
 */
function StiftBild() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.4"
         strokeLinejoin="round">
      <path d="M11.4 1.8l2.8 2.8-8.5 8.5-3.5.7.7-3.5z" />
      <path d="M10 3.2l2.8 2.8" />
    </svg>
  );
}

function AusBild() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="8" r="5.8" />
      <path d="M4 4l8 8" />
    </svg>
  );
}

export default function Leb(
  { ich, auftragId, springe }:
  { ich: Anmelder; auftragId: string;
    /** Auf einen anderen Reiter der Akte wechseln. */
    springe?: (ziel: string) => void }
) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [antwort, setAntwort] = useState<string | null>(null);
  const [erzeugen, setErzeugen] = useState(false);
  const [zurueck, setZurueck] = useState<any>(null);
  const [mengen, setMengen] = useState(false);
  /* Die Zeile, an der gerade getippt wird. Sie steht im ELTERN-Zustand und
     nicht in der Zeile: eine 2. offene Zeile wäre eine 2. Wahrheit über
     dieselbe Liste, und beim Speichern gewönne die zuletzt gedrückte. */
  const [bearb, setBearb] = useState<
    { id: string; menge: string; grund: string; vermerk: string } | null>(null);
  const [deakt, setDeakt] = useState<any>(null);
  /* Die Papiergruppe, deren Liste gerade offen ist. Sie steht als GANZE
     darin und nicht als Schlüssel: das Fenster zeigt genau die Stücke, die
     der Server geliefert hat, und holt sie nicht ein zweites Mal. */
  const [papiere, setPapiere] = useState<any>(null);
  const [neu, setNeu] = useState(false);
  const [lv, setLv] = useState<any[] | null>(null);
  /* Der Leistungszeitraum von Hand (.351): offen = { von, bis } im Fenster. */
  const [lz, setLz] = useState<{ von: string; bis: string } | null>(null);

  const darfExport = darf(ich, 'abrechnung.leb');
  /* WER EINE POSITION NACHTRÄGT, SCHREIBT INS AUFMASS. Der Knopf hängt
     deshalb an dem Recht, das der Weg dahinter verlangt - ein Knopf, der auf
     ein 403 führt, ist schlimmer als kein Knopf. */
  const darfNachtragen = darf(ich, 'aufmass.erfassen');
  /* WER DIE PAPIERE DANEBENLEGEN WILL, MUSS SIE LESEN DÜRFEN. Der Weg
     dahinter (`GET /dateien/<kennung>`) verlangt `datei.lesen`. */
  const darfDatei = darf(ich, 'datei.lesen');

  async function laden() {
    try { setD(await api.leb(auftragId)); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, [auftragId]);
  useFrisch(laden);

  /* DIE QUITTUNG GEHT ÜBER DENSELBEN WEG WIE ALLES ANDERE AUF DIESEM
     BILDSCHIRM: `tu` haelt den Knopf an, faengt den Fehler in den Kasten und
     zieht den Stand danach nach. Ein eigener Weg fuer 1 Knopf waere eine
     zweite Fassung derselben 5 Zeilen. */
  async function quittieren(an: boolean) {
    const e = await tu(() => api.abgerechnetQuittieren(auftragId, an));
    /* UND WAS MIT DEM STAND GESCHAH, STEHT DA (09.10.2026): der Knopf setzt
       den Auftrag auf „erledigt", aus jedem Stand. Eine Automatik, die stumm
       entscheidet, ist von einer fehlenden nicht zu unterscheiden. */
    const ab = e?.abschluss;
    if (ab) {
      setAntwort(ab.setzen
        ? 'Quittiert. Der Auftrag steht jetzt auf „erledigt": ' + ab.grund
        : 'Quittiert. ' + ab.grund);
    }
  }

  async function tu(was: () => Promise<any>) {
    setLaeuft(true); setFehler(null);
    try { const e = await was(); await laden(); return e; }
    catch (f) { setFehler(ausFehler(f)); return null; }
    finally { setLaeuft(false); }
  }

  /* DER KATALOG WIRD ERST GEHOLT, WENN JEMAND IHN BRAUCHT. Er hat einige
     hundert Zeilen und wird auf diesem Bildschirm selten gebraucht; wer ihn
     bei jedem Öffnen mitlädt, bezahlt ihn bei jedem Öffnen. Und er wird nur
     EINMAL geholt: der Leistungskatalog ändert sich nicht, während jemand
     davorsitzt. */
  async function positionNachtragen() {
    setNeu(true);
    if (lv) return;
    try { setLv(await api.lv()); }
    catch (f) { setFehler(ausFehler(f)); }
  }

  if (!darfExport) {
    return <p className="leise">Dafür fehlt das Recht „LEB erzeugen".</p>;
  }
  if (!d) return <div><Fehlerkasten lage={fehler} /></div>;

  const haeltAuf = d.hindernisse.filter((h: any) => h.haeltAuf);
  /* WAS DEN KNOPF SPERRT, IST NICHT DASSELBE WIE WAS ROT AUSSIEHT.
     Eine fertige Abrechnung hält den Export auf (es gibt nichts zu senden)
     und ist kein Mangel. Der Punkt oben folgt deshalb den MÄNGELN, der
     Knopf weiterhin jedem Hindernis. */
  const maengel = haeltAuf.filter((h: any) => !h.fertig);
  // Die Zeilen, an denen sich etwas ändern lässt: was mitgeht UND was auf 0
  // steht. Eine Position, die schon abgerechnet ist, gehört nicht dazu -
  // über sie ist entschieden.
  const zeilen: Anzeigezeile[] = [...d.offen, ...d.ohneMenge];
  const geaendert = zeilen.filter((z) => z.geaendert);

  return (
    <div>
      <div className="reihe" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="reihe" style={{ gap: 8 }}>
          {/* Derselbe Punkt wie im Aufmaß und in der Behördenmappe: rot,
              solange etwas aufhält, orange, solange Positionen offen sind,
              grün, wenn alles abgerechnet ist. */}
          <span className={'zustandspunkt '
            + (maengel.length ? 'hart'
             : d.offen.length ? 'offen'
             : d.schon.length ? 'gut' : 'aus')}
                title={maengel.length ? 'Hält auf'
                  : d.offen.length ? 'Positionen offen'
                  : d.schon.length ? 'Alles abgerechnet'
                  : 'Noch keine Position'} />
          <b>LEB-Export</b>{' '}
          <span className="klein leise">
            {d.offen.length} von {d.offen.length + d.schon.length} Positionen offen
          </span>
        </div>
        <div className="reihe">
          {/* DIE 3 KNÖPFE ZUM DANEBENLEGEN.
              ==============================================================
              ORIGINALTEXT MARVIN (10.09.2026, zu Bild 1)
                „bild 1: links nebn die markierten symbole kommen die neuen
                 knöpfe."

              UND WOZU, STEHT SEIT DEM 06.08.2026 DA
                „Zugriff auf die rotberichtigung haben, denn die lege ich mir
                 immer zur Erstellung des aufmasses auf den zweiten Monitor
                 und dann vergleiche ich."

              ES SIND ECHTE VERWEISE UND KEIN `window.open`. Was der Browser
              ohne Nutzergeste sperren würde, wird gar nicht erst versucht -
              dieselbe Entscheidung wie in der alten Werkbank und wie bei
              „Vorschau ansehen" 3 Knöpfe weiter rechts. Der Reiter lässt
              sich auf den zweiten Bildschirm ziehen; ein geblockter Aufruf
              lässt sich gar nichts.

              WELCHE KNÖPFE ÜBERHAUPT DASTEHEN, ENTSCHEIDET DER SERVER
              (`regeln/lebpapiere.ts`), und er nimmt nur Papiere, deren Datei
              wirklich daliegt. Hier wird nichts nachgeprüft und nichts
              erraten: eine 2. Fassung derselben Entscheidung liefe beim
              ersten Nachtrag auseinander.

              UND SIE HÄNGEN AN `datei.lesen`. Ein Knopf, der auf ein 403
              führt, ist schlimmer als kein Knopf - derselbe Satz wie beim
              Nachtragen. */}
          {/* UND EIN WEG ZUR ROTBERICHTIGUNG, AUCH WENN NOCH KEINE DALIEGT.
              ==============================================================
              ORIGINALTEXT MARVIN (18.09.2026)
                „ich kann die Rotberichtigung bei der erstellung des LEB nicht
                 öffnen. oben fehlt der reiter."

              DIE PAPIERKNÖPFE DANEBEN GIBT ES NUR, WENN DIE DATEI SCHON
              EXISTIERT (`lebpapiere.ts`: eine Gruppe ohne Stücke fällt weg).
              Beim ERSTELLEN des LEB ist die Rotberichtigung aber fast nie
              schon gebaut - genau dann fehlte jeder Weg dorthin. Dieser Knopf
              ist kein Papier, sondern ein Reiterwechsel, und steht deshalb
              unabhängig davon da.

              `aufmass.rotberichtigung`, sonst gar nicht: ein Knopf, der auf
              ein 403 führt, ist schlimmer als kein Knopf.

              UND ER HEISST NICHT WIE DIE DATEI DANEBEN.
              ==============================================================
              ORIGINALTEXT MARVIN (22.09.2026, zum LEB-Reiter)
                „steht 2x Rotberichtigung. du musts das schon richtig
                 deklarieren."

              Er hat recht, und es waren 2 verschiedene Dinge unter einem
              Wort: dieser Knopf WECHSELT DEN REITER, der andere ÖFFNET DIE
              FERTIGE PDF. Sobald eine Mappe gebaut war, standen beide
              nebeneinander und hiessen gleich. **Zwei Knöpfe mit derselben
              Aufschrift und verschiedener Wirkung sind schlimmer als ein
              fehlender:** man drückt einmal den falschen und traut danach
              keinem von beiden mehr. */}
          {springe && darf(ich, 'aufmass.rotberichtigung') && (
            <button type="button" className="knopf"
                    onClick={() => springe('rb')}
                    title={'Den Reiter „Rotberichtigung" öffnen: Abgabe '
                           + 'wählen, Dateien prüfen, Mappe bauen'}>
              Rotberichtigung zusammenstellen
            </button>
          )}
          {darfDatei && (d.papiere ?? []).map((g: any) => (
            g.stuecke.length === 1 ? (
              <a key={g.schluessel} className="knopf"
                 href={api.dateiAdresse(g.stuecke[0].bezugId,
                                        g.stuecke[0].dateiname)}
                 target="_blank" rel="noreferrer"
                 title={g.stuecke[0].dateiname
                        + (g.stuecke[0].zusatz ? ', ' + g.stuecke[0].zusatz : '')}>
                {g.titel}
              </a>
            ) : (
              /* BEI MEHREREN ÖFFNET DER KNOPF DIE LISTE UND NICHT DIE ERSTE
                 DATEI. Welche „die erste" wäre, entschiede eine Sortierung -
                 und wer 4 Nachweise hat, will sehen, welche 4 es sind. Die
                 Zahl steht am Knopf, damit das schon vor dem Drücken
                 dasteht. */
              <button key={g.schluessel} className="knopf" type="button"
                      onClick={() => setPapiere(g)}>
                {g.titel}
                <span className="merker">{g.stuecke.length}</span>
              </button>
            )
          ))}
          {/* EINE POSITION NACHTRAGEN HEISST INS AUFMASS SCHREIBEN.
              ==============================================================
              ORIGINALTEXT MARVIN (10.09.2026)
                „ich brauceh zugriff auf den RV als dropdown, um positionen
                 hinzufügen zu können"

              SIE GEHT ÜBER DEN BESTEHENDEN WEG (`POST /aufmass/position`) und
              NICHT über einen zweiten, LEB-eigenen. `positionenBauen` liest
              ohnehin aus `aufmass_position`; ein zweiter Weg zu denselben
              Zahlen liefe beim ersten Nachtrag auseinander.

              DER KNOPF HÄNGT DESHALB AN `aufmass.erfassen` und nicht am
              Exportrecht: **ein Knopf, der auf ein 403 führt, ist schlimmer
              als kein Knopf.** */}
          {darfNachtragen && (
            <button className="knopf" disabled={laeuft}
                    onClick={() => positionNachtragen()}>
              Position hinzufügen
            </button>
          )}
          {/* WER EINE MENGE ÄNDERN DARF, MUSS SEHEN, WOVON ER ABWEICHT.
              Der Knopf steht neben der Vorschau und nicht in einem Klappmenü:
              er gehört zu dem, was in der Tabelle darunter steht. */}
          {(d.offen.length > 0 || d.ohneMenge.length > 0) && (
            <button className="knopf" disabled={laeuft}
                    onClick={() => setMengen(true)}>
              Mengen ändern
              {geaendert.length > 0 && (
                <span className="merker warnung">{geaendert.length}</span>
              )}
            </button>
          )}
          {d.offen.length > 0 && (
            <a className="knopf" href={api.lebVorschauAdresse(auftragId)}
               target="_blank" rel="noreferrer">
              Vorschau ansehen
            </a>
          )}
          {/* DIE QUITTUNG VON HAND.
              ==============================================================
              ORIGINALTEXT MARVIN (18.09.2026)
                „und im auftrag unter dem LEB brauche ich einen button,
                 auftrag abgerechnet, damit ich das manuell quittieren kann
                 und der filter richtig stimmt!"

              SIE ERSETZT DIE RECHNUNG NICHT, SIE ERGÄNZT SIE. Eine Regel
              über 400 Aufträge trifft Fälle, an die niemand gedacht hat -
              und dann steht der Innendienst vor einem Knopf, den er nicht
              drücken kann. Hier hat der Mensch das letzte Wort.

              DERSELBE KNOPF NIMMT SIE ZURÜCK. Ein Zustand, den man nur in
              eine Richtung ändern kann, ist eine Falle; und er SAGT, ob er
              schon gedrückt ist - ein Knopf, der das verschweigt, wird
              zweimal gedrückt. */}
          {darf(ich, 'abrechnung.leb') && (
            <button className={'knopf' + (d.quittung?.am ? ' f-fertig' : '')}
                    disabled={laeuft}
                    title={d.quittung?.am
                      ? `Quittiert von ${d.quittung.name ?? 'unbekannt'}. `
                        + 'Noch einmal tippen nimmt die Quittung zurück.'
                      : 'Diesen Auftrag von Hand als abgerechnet quittieren. '
                        + 'Er fällt damit aus „Abrechenbar" heraus.'}
                    onClick={() => void quittieren(!d.quittung?.am)}>
              {d.quittung?.am ? 'Abgerechnet - zurücknehmen'
                              : 'Auftrag abgerechnet'}
            </button>
          )}
          <button className="knopf haupt" disabled={laeuft || !!haeltAuf.length}
                  onClick={() => setErzeugen(true)}>
            Datei erzeugen
          </button>
        </div>
      </div>

      {/* UND SIE STEHT DA, WENN SIE GESETZT IST. Ein Zustand, der nur in
          einem Knopftitel steht, ist für den, der die Seite aufmacht, nicht
          vorhanden. */}
      {d.quittung?.am && (
        <div className="hinweis">
          <b>Von Hand quittiert</b>
          Dieser Auftrag steht als abgerechnet
          {d.quittung.name ? ` (${d.quittung.name}` : ''}
          {d.quittung.am ? `, ${zeitpunkt(d.quittung.am)})` : ')'}. Er
          erscheint deshalb nicht mehr unter „Abrechenbar" - unabhängig
          davon, was die Rechnung sagt. Die Quittung lässt sich oben wieder
          zurücknehmen.
        </div>
      )}

      <Fehlerkasten lage={fehler} />
      {antwort && <div className="hinweis">{antwort}</div>}

      {/* DREI GESTALTEN, WEIL ES DREI AUSSAGEN SIND.
          ==================================================================
          ORIGINALTEXT MARVIN (14.09.2026, zu Bild 1)
            „diese rote meldung ist doch der normale fall, wenn alles
             abgerechnet ist. das sieht aus wie ein fehler."

          „Fertig" hält den Export genauso auf wie ein Mangel - eine leere
          Datei geht nicht ins Portal -, und es ist trotzdem das Gegenteil
          davon. Der Server sagt, welche der drei es ist (`fertig`); die
          Oberfläche rechnet es nicht nach, sonst stünde die Regel zweimal
          im Haus. */}
      {d.hindernisse.map((h: any, i: number) => (
        <div key={i} className={'hinweis '
          + (h.fertig ? 'erledigt' : h.haeltAuf ? 'fehler' : 'warnung')}>
          <b>{h.fertig ? 'Nichts zu tun: '
            : h.haeltAuf ? 'Hält auf: ' : 'Zum Nachsehen: '}</b>{h.text}
        </div>
      ))}
      {/* DER EINBLASSTOP GEHÖRT AUF DIE LEB-SEITE (.385). ORIGINALTEXT MARVIN
          (16.10.2026): „in der LEB wäre der hinweis nochmal gut, weil ja dann
          teil und -schluss aufmaß erstellt wird." In der Akte verschwindet
          der Kasten mit dem Abschluss der Gewerke (.384); hier bleibt er,
          solange ein Stop am Auftrag steht, denn er entscheidet über Teil-
          und Schlussaufmaß. */}
      {d.einblasstop && (
        <div className={'hinweis ' + (d.einblasstop.zurueck_am ? '' : 'warnung')}>
          <b>Einblasstop{d.einblasstop.zurueck_am ? ' (zurückgenommen)' : ''}:</b>{' '}
          {d.einblasstop.meter} m{d.einblasstop.seite ? `, ${d.einblasstop.seite}` : ''}, gemeldet {datum(d.einblasstop.gemeldet_am)}
          {d.einblasstop.an_telekom_am ? `, an die Telekom gemeldet am ${datum(d.einblasstop.an_telekom_am)}` : ', noch nicht an die Telekom gemeldet'}
          {d.einblasstop.zurueck_am ? `, zurückgenommen am ${datum(d.einblasstop.zurueck_am)}${d.einblasstop.zurueck_grund ? ` (${d.einblasstop.zurueck_grund})` : ''}` : ''}.
          {' '}{d.einblasstop.zurueck_am
            ? 'Der Nachtrag geht als Schlussaufmaß mit Schlussmarke.'
            : 'Erst das Teilaufmaß ohne Schlussmarke; der Nachtrag nach der Behebung als Schlussaufmaß.'}
        </div>
      )}

      {/* WAS DER BILDSCHIRM AUF EINEN BLICK SAGEN MUSS.
          ==================================================================
          ORIGINALTEXT MARVIN (03.09.2026)
            „du sollst das dynamischer, innovativer und heller gestalten."

          Dieselbe Antwort wie beim Systemzustand und beim Aufmaß: die Zahlen,
          die man beim Öffnen sucht, stehen oben - und die eine, die aufhält
          (die fehlende Belegnummer), trägt Farbe. Sie steht damit ZWEIMAL da:
          als roter Kasten mit dem Grund und als Kachel mit dem Zustand. Das
          ist keine Wiederholung, sondern zwei verschiedene Fragen: „warum
          geht es nicht" und „woran stehe ich gerade". */}
      <div className="kachelreihe" style={{ marginBottom: 14 }}>
        <div className="kachel">
          <div className="wert">{d.offen.length}</div>
          <div className="was">
            {d.offen.length === 1 ? 'Position geht mit' : 'Positionen gehen mit'}
          </div>
        </div>
        <div className={'kachel ' + (d.belegnummer ? 'gut' : 'hart')}>
          <div className="wert einfarbig">{d.belegnummer ?? 'fehlt'}</div>
          <div className="was">
            Belegnummer{d.belegnummer ? '' : ' – kommt aus dem Abruf'}
          </div>
        </div>
        <div className={'kachel '
          + (d.zeitraum.von && d.zeitraum.bis ? 'gut' : 'offen')}>
          <div className="wert" style={{ fontSize: 17 }}>
            {d.zeitraum.von && d.zeitraum.bis
              ? `${datum(d.zeitraum.von)} – ${datum(d.zeitraum.bis)}`
              : 'unvollständig'}
          </div>
          <div className="was">Leistungszeitraum</div>
        </div>
      </div>

      {/* Der Kopf der Datei. Was hier steht, geht so ins Portal. */}
      <table className="tabelle" style={{ marginBottom: 18 }}>
        <tbody>
          <tr>
            <td style={{ width: 200 }}>Belegnummer</td>
            <td className="einfarbig">
              {d.belegnummer ?? <span className="leise">fehlt</span>}
              <span className="klein leise" style={{ marginLeft: 8 }}>
                Position {d.belegposition}
              </span>
            </td>
          </tr>
          <tr>
            <td>Leistungszeitraum</td>
            <td>
              {d.zeitraum.von && d.zeitraum.bis
                ? <>{datum(d.zeitraum.von)} bis {datum(d.zeitraum.bis)}</>
                : <span className="leise">nicht vollständig</span>}
              {/* Ein Datum ohne seine Herkunft laesst sich spaeter nicht mehr
                  nachvollziehen. */}
              <div className="klein leise">
                Beginn: {d.zeitraum.quelleVon}, Ende: {d.zeitraum.quelleBis}
              </div>
              {/* VON HAND SETZEN (.351). Marvin, 12.10.2026: „wenn kein
                  Leistungszeitraum erkannt wurde habe ich keine möglichkeit
                  das LEB zu erstellen. ich kann es aber nicht manuell
                  ergänzen. diese funktion wird benötigt." */}
              {darfExport && (
                <div className="reihe" style={{ gap: 8, marginTop: 6 }}>
                  <button type="button" className="knopf klein" disabled={laeuft}
                          onClick={() => setLz({ von: d.zeitraum.von ?? '', bis: d.zeitraum.bis ?? '' })}>
                    {d.zeitraum.vonHand ? 'Ändern' : 'Von Hand setzen'}
                  </button>
                  {d.zeitraum.vonHand && (
                    <button type="button" className="knopf klein" disabled={laeuft}
                            onClick={() => tu(() => api.lebZeitraum(auftragId, null, null))}>
                      Wie erkannt
                    </button>
                  )}
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td>Baustelle</td>
            <td>{d.anschrift}{d.ort ? `, ${d.ort}` : ''}</td>
          </tr>
          {/* DIE BEIDEN KENNUNGEN, MIT DENEN DIE TELEKOM ZURÜCKFRAGT.
              ==============================================================
              ORIGINALTEXT MARVIN (10.09.2026)
                „ich brauche oben noch die info der KLS und SM Nummer im KLS
                 Export."
                „dann brauche ich überall eine onclick-zwischenablage für sm,
                 kls-id mit kurzer info, dass es in die zwischenablage kopiert
                 ist."

              DIE KLS STAND ALS BEISATZ HINTER DER ANSCHRIFT, in kleiner
              grauer Schrift - also an der Stelle, an der man eine Fussnote
              liest und keine Nummer sucht. Die SM stand gar nicht da.

              BEIDE STEHEN JETZT IN EIGENEN ZEILEN UND SIND MIT EINEM KLICK
              IN DER ZWISCHENABLAGE. Wer diese Datei hochlädt, hat das Portal
              daneben offen und tippt beide Nummern ab; beim dritten Mal
              tippt er daneben und sucht den Fehler danach im Portal. */}
          <tr>
            <td>KLS-ID</td>
            <td><Kopierbar wert={d.kls} was="KLS-ID" /></td>
          </tr>
          <tr>
            <td>SM-Nummer</td>
            <td><Kopierbar wert={d.sm} was="SM-Nummer" /></td>
          </tr>
          <tr>
            <td>Vorschlag</td>
            <td>
              <b>{ARTEN.find((a) => a.wert === d.vorschlag.art)?.text}</b>
              <div className="klein leise">{d.vorschlag.grund}</div>
            </td>
          </tr>
        </tbody>
      </table>

      <h4 style={{ margin: '0 0 6px' }}>Was in die Datei kommt</h4>
      {!d.offen.length && (
        <p className="leise">Keine Position offen.</p>
      )}
      {/* DIE GEGENÜBERSTELLUNG.
          ==================================================================
          ORIGINALTEXT MARVIN (08.09.2026)
            „dazu die Gegenüberstellung zu dem, was unser System gerechnet
             hat"

          3 Zahlen nebeneinander, und jede beantwortet eine andere Frage: was
          hat der Partner GEMELDET, was hat der Innendienst GEPRÜFT, und was
          wird ABGERECHNET. Gerechnet wird die geprüfte Menge, und nur wo
          keine steht, die gemeldete - diese Rechnung stand bisher auf keinem
          Bildschirm, und die Datei trug am Ende eine Zahl, deren Herkunft
          niemand sah. */}
      {d.offen.length > 0 && (
        <table className="tabelle">
          <thead>
            {/* DIE LEISTUNGSNUMMER ALLEIN SAGT NIEMANDEM ETWAS.
                ============================================================
                ORIGINALTEXT MARVIN (10.09.2026)
                  „außerdem soll beim LEB export neben der Leistungsnummer
                   auch die Bezeichnung laut rahmenvertrag stehen."

                „10030364" ist eine Zahl, „Zulage Hausanschluss >1 m" ist
                eine Leistung. Wer eine Menge prüft, bevor sie hinausgeht,
                prüft die Leistung und nicht die Nummer - und die stand hier
                nicht, obwohl der Text die ganze Zeit mitkam.

                UND „VERMERK" HEISST HIER VERMERK, UND SONST NICHTS.
                Marvin, 10.09.2026: „bei der spalte «Vermerk» machst du den
                restlichen text weg, die 1." Dort stand „Vermerk
                (sectionText)" - der Feldname der Datei in Klammern daneben.
                Er war für den gedacht, der eine Meldung des Portals liest und
                die Spalte wiederfinden will; auf dem Bildschirm, an dem man
                ARBEITET, ist er die Klammer, die man jedes Mal überliest.
                Der Feldname steht im Handbuch.

                UND DIE LEISTUNG BEKOMMT EINE BREITE. Marvin: „der abstan der
                der 2. und 3. spalte ist zu groß." Sie trug als einzige keine,
                also bekam sie den ganzen übrigen Platz und schob die
                Zahlenspalten an den rechten Rand - **zwischen der Leistung
                und der Menge, die zu ihr gehört, lag die halbe
                Tabellenbreite.** Jetzt trägt sie 34 %, und der Rest geht an
                den Vermerk. */}
            <tr>
              <th style={{ width: 110 }}>OZ</th>
              <th style={{ width: '34%' }}>Leistung laut Rahmenvertrag</th>
              <th style={{ width: 84, textAlign: 'right' }}>Gemeldet</th>
              <th style={{ width: 84, textAlign: 'right' }}>Geprüft</th>
              <th style={{ width: 120, textAlign: 'right' }}>Abgerechnet</th>
              <th>Vermerk</th>
              <th style={{ width: 92 }} />
            </tr>
          </thead>
          <tbody>
            {d.offen.map((p: any) => {
              const id: string | undefined = p.aufmassPositionId;
              const dran = !!bearb && !!id && bearb.id === id;
              /* Die getippte Zahl wird GERECHNET und nicht angesehen. Ob sie
                 abweicht, entscheidet der Vergleich mit der gerechneten
                 Menge - und eine Menge, die auf dem gerechneten Wert steht,
                 IST keine Änderung (`listeMitZeile` nimmt sie zurück). */
              const getippt = dran ? mengeAusFeld(bearb!.menge) : null;
              const weicht = dran && getippt !== null
                          && getippt !== p.mengeGerechnet;
              const fehltGrund = weicht && !bearb!.grund.trim();
              /* DER VERMERK BRAUCHT KEINEN GRUND. Er GEHT IN DIE DATEI und
                 trägt seine Begründung damit selbst; die Menge geht als
                 blosse Zahl hinaus und braucht den Satz daneben. */
              const vermerkWeicht = dran
                && bearb!.vermerk.trim()
                   !== (p.sectionTextGerechnet ?? '').trim();
              return (
                <Fragment key={p.schluessel}>
                  <tr>
                    <td><Kopierbar wert={p.oz} was="OZ" klein /></td>
                    <td>
                      {p.text || <span className="leise">nicht im LV</span>}
                      {/* EIN FELDNAME IST KEINE BESCHRIFTUNG.
                          ==================================================
                          ORIGINALTEXT MARVIN (14.09.2026)
                            „longText: kommt weg"

                          Hier stand „longText: <Wert>", also der Name der
                          Spalte in der CSV. Für den, der eine Meldung des
                          Portals liest, ist das eine Auskunft; auf dem
                          Bildschirm, an dem gearbeitet wird, ist es die
                          Zeile, die man jedes Mal überliest - dieselbe
                          Entscheidung wie bei „Vermerk (sectionText)" am
                          10.09.2026. Der Feldname steht im Handbuch.

                          UND MEISTENS SAGTE SIE OHNEHIN NICHTS. `longText`
                          trägt den Namen des Abschnitts, und der steht eine
                          Spalte weiter unter „Vermerk"; ohne Abschnitt ist
                          es der Positionstext selbst, der DARÜBER steht. Es
                          bleibt GENAU EIN Fall, in dem er etwas eigenes
                          sagt: die Gebührenzeile trägt dort den Satz, der
                          die Summe aufschlüsselt. Der bekommt eine
                          Beschriftung für Menschen. */}
                      {p.longText && p.longText !== p.text
                        && p.longText !== p.sectionText && (
                        <div className="klein leise">
                          Kommentar an die Telekom: {p.longText}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }} className="leise">
                      {p.mengeSub === null ? '–' : menge(p.mengeSub)}
                    </td>
                    <td style={{ textAlign: 'right' }} className="leise">
                      {p.mengeMds === null ? '–' : menge(p.mengeMds)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {dran ? (
                        <input
                          className={getippt === null ? 'falsch' : undefined}
                          style={{ width: 96, textAlign: 'right' }}
                          value={bearb!.menge} disabled={laeuft} autoFocus
                          onChange={(x) => setBearb(
                            { ...bearb!, menge: x.target.value })} />
                      ) : (
                        <>
                          <b>{menge(p.menge)}</b>
                          {p.geaendert && (
                            <>
                              <span className="merker warnung">von Hand</span>
                              {/* Ein Grund, der nicht dasteht, ist keiner: er
                                  steht an der Zeile und nicht nur im
                                  Protokoll. */}
                              <div className="klein leise">
                                statt {menge(p.mengeGerechnet)}, {p.grund}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </td>
                    {/* DER VERMERK IST ÄNDERBAR, DER LANGTEXT NICHT.
                        ======================================================
                        ORIGINALTEXT MARVIN (14.09.2026)
                          „den vermerk muss ich je position ändern können."

                        Er steht im selben Feld wie die Menge - dieselbe
                        Aufmasszeile, derselbe Lauf, dieselbe Rücknahme. Und
                        wie bei der Menge steht der GERECHNETE Wert daneben
                        und nicht an seiner Stelle: eine Angabe, die eine
                        andere ersetzt, nimmt die Frage mit, die später
                        gestellt wird - wovon weicht sie ab. */}
                    <td className="klein">
                      {dran ? (
                        <input
                          style={{ width: '100%', minWidth: 0 }}
                          maxLength={d.vermerkHoechstens ?? undefined}
                          value={bearb!.vermerk} disabled={laeuft}
                          placeholder="Vermerk (geht in die Datei)"
                          onChange={(x) => setBearb(
                            { ...bearb!, vermerk: x.target.value })} />
                      ) : (
                        <>
                          {p.sectionText || <span className="leise">leer</span>}
                          {p.vermerkGeaendert && (
                            <>
                              <span className="merker warnung">von Hand</span>
                              <div className="klein leise">
                                statt {p.sectionTextGerechnet
                                       || '(kein Abschnitt)'}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </td>
                    {/* DIE BEIDEN HANDGRIFFE AM ENDE DER ZEILE.
                        ======================================================
                        ORIGINALTEXT MARVIN (10.09.2026)
                          „am ende jeder spalte machst du ein symbol um direkt
                           dort live zu ändern und ein symbol um die spalte zu
                           löschen, bzw. deaktiereven, so dass sie nicht in
                           die csv kommt."

                        SIE STEHEN NUR DA, WO SIE ETWAS BEWIRKEN KÖNNEN. Eine
                        Zeile ohne `aufmassPositionId` hat keine Aufmasszeile
                        dahinter (die Gebühr wird gerechnet); an ihr lässt
                        sich nichts setzen, und ein Knopf, der auf ein 403
                        führt, ist schlimmer als kein Knopf. */}
                    <td className="knopfleiste">
                      {id && !dran && (
                        <>
                          <button className="knopf klein" disabled={laeuft}
                                  title="Menge und Vermerk ändern"
                                  aria-label="Menge und Vermerk ändern"
                                  onClick={() => setBearb({
                                    id, menge: feldAusMenge(p.menge),
                                    grund: p.grund ?? '',
                                    vermerk: p.sectionText ?? '',
                                  })}>
                            <StiftBild />
                          </button>
                          <button className="knopf klein warnung" disabled={laeuft}
                                  title="Nicht abrechnen"
                                  aria-label="Nicht abrechnen"
                                  onClick={() => setDeakt(p)}>
                            <AusBild />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {/* DER GRUND STEHT NICHT IN DER SPALTE „VERMERK".
                      ========================================================
                      Der Vermerk ist `sectionText` und GEHT IN DIE DATEI; der
                      Grund einer Abweichung bleibt im Haus („keine interne
                      Bemerkung in die Datei"). Zwei verschiedene Dinge in
                      einer Spalte wären eine Spalte, die zwei Fragen
                      beantwortet - und dann verliert sie eine davon. Er
                      bekommt deshalb seine eigene Zeile darunter. */}
                  {dran && (
                    <tr>
                      <td colSpan={7}>
                        <div className="reihe" style={{ gap: 8 }}>
                          <input
                            className={fehltGrund ? 'falsch' : undefined}
                            style={{ flex: 1, minWidth: 0 }}
                            value={bearb!.grund} disabled={laeuft}
                            placeholder={weicht
                              ? 'Grund der Abweichung – Pflicht, bleibt im Haus'
                              : 'Grund der Abweichung (bleibt im Haus)'}
                            onChange={(x) => setBearb(
                              { ...bearb!, grund: x.target.value })} />
                          <button
                            className="knopf haupt"
                            disabled={laeuft || getippt === null || fehltGrund}
                            onClick={async () => {
                              const e = await tu(() => api.lebMengen(
                                auftragId,
                                listeMitZeile(zeilen, bearb!.id,
                                              getippt as number, bearb!.grund,
                                              bearb!.vermerk)));
                              if (e) {
                                /* WAS DIE MELDUNG NENNT, IST DAS, WAS
                                   WIRKLICH ABWEICHT. Beide Angaben hängen an
                                   derselben Zeile und können einzeln gesetzt
                                   sein; eine Meldung, die immer von der Menge
                                   spricht, ist bei einer reinen
                                   Vermerkänderung falsch. */
                                const teile: string[] = [];
                                if (weicht) {
                                  teile.push(`${menge(getippt as number)} statt `
                                             + `${menge(p.mengeGerechnet)}`);
                                }
                                if (vermerkWeicht) teile.push('Vermerk gesetzt');
                                setAntwort(teile.length
                                  ? `${p.oz}: ${teile.join(', ')}.`
                                  : `${p.oz} steht wieder auf dem, was das `
                                    + 'System rechnet.');
                                setBearb(null);
                              }
                            }}>
                            {laeuft ? 'Einen Moment' : 'Speichern'}
                          </button>
                          <button className="knopf" disabled={laeuft}
                                  onClick={() => setBearb(null)}>
                            Abbrechen
                          </button>
                        </div>
                        {/* EIN GESPERRTER KNOPF OHNE GRUND SIEHT AUS WIE EIN
                            KAPUTTER. */}
                        {getippt === null && (
                          <div className="klein warnfarbe" style={{ marginTop: 4 }}>
                            Diese Menge lässt sich nicht lesen. Dezimalkomma,
                            keine Einheit.
                          </div>
                        )}
                        {fehltGrund && (
                          <div className="klein warnfarbe" style={{ marginTop: 4 }}>
                            Wer anders abrechnet, als das System gerechnet hat,
                            schreibt dazu, warum – sonst ist die Abrechnung
                            später nicht zu verteidigen.
                          </div>
                        )}
                        {dran && !weicht && !vermerkWeicht
                          && getippt !== null && (
                          <div className="klein leise" style={{ marginTop: 4 }}>
                            Menge und Vermerk stehen auf dem, was das System
                            rechnet ({feldAusMenge(p.mengeGerechnet)},
                            {' '}{p.sectionTextGerechnet || 'kein Abschnitt'}).
                            Speichern nimmt eine frühere Änderung an dieser
                            Zeile zurück.
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {/* 2 GRÜNDE, WARUM EINE POSITION NICHT MITGEHT, UND 2 NÄCHSTE SCHRITTE.
          Ohne Menge im Aufmaß heisst: da fehlt noch etwas. Von Hand auf 0
          gesetzt heisst: jemand hat entschieden - und dann steht sein Grund
          daneben. */}
      {d.ohneMenge.filter((p: any) => !p.geaendert).length > 0 && (
        <p className="klein leise" style={{ marginTop: 8 }}>
          Ohne Menge im Aufmaß und deshalb außen vor:{' '}
          {d.ohneMenge.filter((p: any) => !p.geaendert)
            .map((p: any) => p.oz).join(', ')}
        </p>
      )}
      {d.ohneMenge.filter((p: any) => p.geaendert).length > 0 && (
        <table className="tabelle" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th style={{ width: 110 }}>OZ</th>
              <th style={{ width: 110, textAlign: 'right' }}>Gerechnet</th>
              <th>Von Hand auf 0 gesetzt, weil</th>
              <th style={{ width: 150 }} />
            </tr>
          </thead>
          <tbody>
            {d.ohneMenge.filter((p: any) => p.geaendert).map((p: any) => (
              <tr key={p.schluessel}>
                <td className="einfarbig">{p.oz}</td>
                <td style={{ textAlign: 'right' }} className="leise">
                  {menge(p.mengeGerechnet)}
                </td>
                <td>{p.grund}</td>
                {/* DER WEG ZURÜCK GEHÖRT NEBEN DIE ENTSCHEIDUNG. Wer eine
                    Position auf 0 gesetzt hat und es rückgängig machen will,
                    steht vor DIESER Tabelle - nicht vor der oben, in der die
                    Zeile gar nicht mehr steht. `listeOhneZeile` NIMMT die
                    Änderung weg, statt eine zweite anzulegen, die den
                    gerechneten Wert noch einmal hinschreibt. */}
                <td className="knopfleiste">
                  {p.aufmassPositionId && (
                    <button className="knopf klein" disabled={laeuft}
                            onClick={async () => {
                              const e = await tu(() => api.lebMengen(
                                auftragId,
                                listeOhneZeile(zeilen, p.aufmassPositionId)));
                              if (e) {
                                setAntwort(`${p.oz} geht wieder mit: `
                                  + `${menge(p.mengeGerechnet)}.`);
                              }
                            }}>
                      wieder aufnehmen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* DIE SCHON ABGERECHNETEN POSITIONEN, OFFEN UND VOLLSTÄNDIG (.353).
          ORIGINALTEXT MARVIN (12.10.2026)
            „die bereits abgerechneten pos direkt darstellen und wieder die
             komplette beschreibung mit titel, vermerken. auch der richtige
             name der position anzeigen."
          Vorher zugeklappt und mit dem Abschnitt („Montagegrube 1") an der
          Stelle, an der der Name der Position stehen sollte. */}
      {d.schon.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h4 style={{ margin: '0 0 6px' }}>
            Schon abgerechnet ({d.schon.length})
            <span className="klein leise" style={{ fontWeight: 400, marginLeft: 8 }}>
              geht nicht noch einmal hinaus
            </span>
          </h4>
          <table className="tabelle">
            <thead>
              <tr>
                <th style={{ width: 110 }}>OZ</th>
                <th>Position</th>
                <th style={{ width: 90, textAlign: 'right' }}>Menge</th>
                <th style={{ width: '24%' }}>Vermerk</th>
                <th style={{ width: 190 }}>Abgerechnet in</th>
              </tr>
            </thead>
            <tbody>
              {d.schon.map((p: any) => (
                <tr key={p.schluessel}>
                  <td><Kopierbar wert={p.oz} was="OZ" klein /></td>
                  <td>
                    {p.text || <span className="leise">nicht im LV</span>}
                    {p.longText && p.longText !== p.text && p.longText !== p.sectionText && (
                      <div className="klein leise">Kommentar an die Telekom: {p.longText}</div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}><b>{menge(p.menge)}</b></td>
                  <td className="klein">{p.sectionText || <span className="leise">leer</span>}</td>
                  <td className="klein leise">
                    {(p.abgerechnetIn ?? []).length
                      ? (p.abgerechnetIn as any[]).map((r, i) => (
                          <div key={i}>{r.dateiname ?? 'LEB'}{r.am ? `, ${datum(r.am)}` : ''}</div>
                        ))
                      : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d.frueher.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4 style={{ margin: '0 0 6px' }}>Schon erzeugt</h4>
          <table className="tabelle">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Art</th><th>Datei</th>
                <th style={{ width: 170 }}>Zeitraum</th>
                <th style={{ width: 80 }}>Posten</th>
                <th style={{ width: 160 }}>Erzeugt</th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {d.frueher.map((e: any) => (
                <tr key={e.id}>
                  <td>
                    {ART_KURZ[e.art] ?? e.art}
                    {!e.final_mark && <span className="merker warnung">offen</span>}
                  </td>
                  <td>
                    {e.datei
                      ? <a href={api.dateiAdresse(e.datei, e.dateiname)} target="_blank"
                           rel="noreferrer">{e.dateiname}</a>
                      : e.dateiname}
                  </td>
                  <td className="klein">
                    {datum(e.lz_von)} bis {datum(e.lz_bis)}
                  </td>
                  <td>{e.positionen}</td>
                  <td className="klein leise">
                    {zeitpunkt(e.erzeugt_am)}
                    {e.erzeugt_von && <div>{e.erzeugt_von}</div>}
                  </td>
                  <td>
                    <button className="knopf klein" disabled={laeuft}
                            onClick={() => setZurueck(e)}>
                      zurücknehmen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="klein leise" style={{ marginTop: 6 }}>
            Zurücknehmen macht die enthaltenen Positionen wieder offen. Der Weg
            für eine Datei, die das Portal abgewiesen hat.
          </p>
        </div>
      )}

      {erzeugen && (
        <ErzeugenDialog
          d={d} laeuft={laeuft} fehler={fehler} zu={() => setErzeugen(false)}
          erzeuge={async (b) => {
            const e = await tu(() => api.lebErzeugen(auftragId, b));
            if (e) {
              /* ====================================================
               * UND WAS DER AUFTRAG JETZT IST
               * ====================================================
               * ORIGINALTEXT MARVIN (22.09.2026)
               *   „was auch noch nicht implementiert ist, wenn die
               *    Rotbreichtigung / LEB erstellt wurde, dass der
               *    auftrag auf «erledigt» im status automatisch
               *    umgestellt wird."
               *
               * **ES IST IMPLEMENTIERT - ES HAT NUR NIE JEMAND
               * ERFAHREN.** Der Server prüft nach jeder erzeugten
               * Datei, ob der Auftrag abgeschlossen ist, setzt ihn
               * auf „erledigt" und schreibt es in die Akte. Bleibt
               * er offen, nennt er den Grund („es gibt noch keine
               * Rotberichtigung", „3 Positionen sind noch nicht
               * abgerechnet") - und diese Antwort hat kein einziger
               * Bildschirm gelesen.
               *
               * Eine Automatik, die stumm entscheidet, ist von einer
               * fehlenden Automatik nicht zu unterscheiden.
               */
              /* UND DIE DATEI KOMMT GLEICH MIT (.353). Marvin, 12.10.2026:
                 „kannst du die csv nach dem bestellen direkt runterladen".
                 Derselbe Weg wie der Link unter „Schon erzeugt"; der Browser
                 lädt sie herunter, statt sie anzuzeigen. */
              if (e.datei) {
                const a = document.createElement('a');
                a.href = api.dateiAdresse(e.datei, e.dateiname);
                a.download = e.dateiname ?? 'leb.csv';
                document.body.appendChild(a); a.click(); a.remove();
              }
              const ab = e.abschluss;
              setAntwort(`${e.dateiname} mit ${e.positionen} Positionen. `
                + (e.datei ? 'Die Datei wird heruntergeladen. ' : '')
                + e.hinweis
                + (ab ? (ab.setzen
                    ? ' Der Auftrag steht jetzt auf „erledigt": ' + ab.grund
                    : ' Der Auftrag bleibt offen: ' + ab.grund) : ''));
              setErzeugen(false);
            }
          }}
        />
      )}

      {mengen && (
        <MengenDialog
          zeilen={zeilen} laeuft={laeuft} fehler={fehler}
          /* DIE ZAHL KOMMT VOM SERVER UND WIRD DURCHGEREICHT. Ein
             Vorgabewert an dieser Stelle waere die 2. Fassung derselben
             Grenze - und die faellt an dem Tag auf, an dem sie schon
             falsch war. */
          hoechstens={d.vermerkHoechstens ?? null}
          zu={() => setMengen(false)}
          speichere={async (liste) => {
            const e = await tu(() => api.lebMengen(auftragId, liste));
            if (e) {
              setAntwort(liste.length
                ? `${liste.length} Zeile${liste.length === 1 ? '' : 'n'} von `
                  + 'Hand gesetzt. Vorschau und Datei zeigen ab jetzt diesen '
                  + 'Stand.'
                : 'Mengen und Vermerke stehen wieder auf dem, was das System '
                  + 'rechnet.');
              setMengen(false);
            }
          }}
        />
      )}

      {/* NICHT ABRECHNEN HEISST MENGE 0, UND DER GRUND IST PFLICHT.
          ==================================================================
          Es entsteht KEIN zweiter Merker neben `leb_aenderung`: eine Menge
          von 0 heisst in diesem Haus seit jeher „diese Position geht nicht
          mit", und der Weg dahin ist derselbe wie bei jeder anderen Menge.
          Ein eigenes Feld daneben wäre eine zweite Wahrheit über dieselbe
          Frage - und die erste, die beim nächsten Nachtrag vergessen wird. */}
      {deakt && (
        <MitGrund
          titel={`${deakt.oz} nicht abrechnen`} knopf="Nicht abrechnen"
          erklaerung="Die Position geht mit der Menge 0 in die Datei, steht
                      also nicht in der Abrechnung. Das Aufmaß bleibt
                      unangetastet, und der Grund steht danach an der Zeile."
          laeuft={laeuft} fehler={fehler} zu={() => setDeakt(null)}
          fertig={async (grund) => {
            /* DER VERMERK BLEIBT, WIE ER STEHT. „Nicht abrechnen" ist eine
               Aussage über die MENGE; wer hier den gerechneten Vermerk
               mitschickte, nähme einer Zeile nebenbei das Wort weg, das
               jemand von Hand gesetzt hat. */
            const e = await tu(() => api.lebMengen(
              auftragId,
              listeMitZeile(zeilen, deakt.aufmassPositionId, 0, grund,
                            deakt.sectionText ?? '')));
            if (e) {
              setAntwort(`${deakt.oz} geht nicht mit in die Datei.`);
              setDeakt(null);
            }
          }}
        />
      )}

      {/* DIE LISTE HINTER EINEM DER 3 KNÖPFE.
          ==============================================================
          Sie steht nur da, wenn eine Gruppe MEHRERE Papiere trägt. Bei
          einem geht der Knopf direkt auf die Datei: ein Fenster mit genau
          einer Zeile ist ein Klick, der nichts entscheidet.

          JEDE ZEILE IST EIN ECHTER VERWEIS, und das Fenster bleibt dabei
          offen. Wer 4 Nachweise vergleicht, macht sie nacheinander auf und
          soll nicht 4-mal denselben Knopf suchen. */}
      {lz && d && (
        <Dialog titel="Leistungszeitraum von Hand" breite={520} laeuft={laeuft} zu={() => setLz(null)}>
          <Fehlerkasten lage={fehler} />
          <p className="klein leise" style={{ marginTop: 0 }}>
            Erkannt: Beginn {d.zeitraum.erkannt?.von ? `${datum(d.zeitraum.erkannt.von)} (${d.zeitraum.erkannt.quelleVon})` : 'nicht gefunden'},
            Ende {d.zeitraum.erkannt?.bis ? `${datum(d.zeitraum.erkannt.bis)} (${d.zeitraum.erkannt.quelleBis})` : 'nicht gefunden'}.
            Was dem erkannten Datum gleicht oder leer bleibt, folgt weiter der Erkennung.
          </p>
          <div className="reihe" style={{ gap: 12 }}>
            {/* HEUTE MIT EINEM KLICK (.471). Marvin, 23.10.2026: „dass ich heute
                beginnen und heute Ende auch klicken kann, damit ich nicht immer
                das Datum eintragen muss". Deutsche Zeit, wie überall. */}
            <div className="feld"><label>Beginn</label>
              <input type="date" value={lz.von} onChange={(e) => setLz({ ...lz, von: e.target.value })} />
              <button type="button" className="knopf klein" style={{ marginTop: 4 }} onClick={() => setLz({ ...lz, von: heuteIso() })}>Heute</button></div>
            <div className="feld"><label>Ende</label>
              <input type="date" value={lz.bis} onChange={(e) => setLz({ ...lz, bis: e.target.value })} />
              <button type="button" className="knopf klein" style={{ marginTop: 4 }} onClick={() => setLz({ ...lz, bis: heuteIso() })}>Heute</button></div>
            <div className="feld" style={{ alignSelf: 'flex-end' }}>
              <button type="button" className="knopf" onClick={() => setLz({ ...lz, von: heuteIso(), bis: heuteIso() })}>Beides heute</button></div>
          </div>
          {lz.von && lz.bis && lz.bis < lz.von && <p className="klein rot">Das Ende liegt vor dem Beginn.</p>}
          <div className="reihe" style={{ marginTop: 10 }}>
            <button type="button" className="knopf haupt"
                    disabled={laeuft || (!lz.von && !lz.bis) || (!!lz.von && !!lz.bis && lz.bis < lz.von)}
                    onClick={async () => {
                      const e = await tu(() => api.lebZeitraum(auftragId,
                        lz.von && lz.von !== d.zeitraum.erkannt?.von ? lz.von : null,
                        lz.bis && lz.bis !== d.zeitraum.erkannt?.bis ? lz.bis : null));
                      if (e) { setLz(null); setAntwort(e.klartext ?? 'Gespeichert.'); }
                    }}>Speichern</button>
            <button type="button" className="knopf" onClick={() => setLz(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}

      {papiere && (
        <Dialog titel={papiere.titel} breite={620}
                untertitel={`${papiere.stuecke.length} Dateien, jede öffnet
                             in einem eigenen Reiter`}
                zu={() => setPapiere(null)}>
          <table className="tabelle">
            <tbody>
              {papiere.stuecke.map((s: any) => (
                <tr key={s.bezugId}>
                  <td>
                    <a href={api.dateiAdresse(s.bezugId, s.dateiname)}
                       target="_blank" rel="noreferrer">{s.dateiname}</a>
                  </td>
                  <td className="klein leise" style={{ width: 160 }}>
                    {s.zusatz}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Dialog>
      )}

      {/* DER LEISTUNGSKATALOG WIRD GEHOLT, UND SOLANGE STEHT DAS DA.
          Ein Fenster, das erst erscheint, wenn die Antwort da ist, sieht
          nach einem Knopf aus, der nichts tut. */}
      {neu && (lv ? (
        <NeuePosition
          lv={lv} abschnitte={d.abschnitte ?? []} laeuft={laeuft} fehler={fehler} vermerk
          zu={() => setNeu(false)}
          speichere={async (b: any) => {
            const e = await tu(() => api.aufmassPosition(auftragId, b));
            if (e) {
              setAntwort(`${b.oz} ist im Aufmaß nachgetragen und steht ab `
                       + 'jetzt in dieser Liste.'
                       + (String(b.vermerk ?? '').trim() ? ` Vermerk „${String(b.vermerk).trim()}" geht so in die Datei.` : ''));
              setNeu(false);
            }
          }}
        />
      ) : (
        <Dialog titel="Position hinzufügen" laeuft zu={() => setNeu(false)}
                breite={620}>
          <Fehlerkasten lage={fehler} />
          <p className="leise">Der Leistungskatalog wird geladen.</p>
        </Dialog>
      ))}

      {zurueck && (
        <MitGrund
          titel={`${zurueck.dateiname} zurücknehmen`} knopf="Zurücknehmen"
          erklaerung="Die enthaltenen Positionen gelten danach wieder als offen
                      und gehen beim nächsten Export erneut hinaus. Die Datei
                      selbst bleibt liegen."
          laeuft={laeuft} fehler={fehler} zu={() => setZurueck(null)}
          fertig={async (grund) => {
            if (await tu(() => api.lebZuruecknehmen(zurueck.id, grund))) {
              setAntwort('Zurückgenommen. Die Positionen sind wieder offen.');
              setZurueck(null);
            }
          }}
        />
      )}
    </div>
  );
}


/**
 * Die Mengen setzen, die WIRKLICH abgerechnet werden.
 *
 * ORIGINALTEXT MARVIN (08.09.2026)
 *   „LEB-Export: editierbar machen, dazu die Gegenüberstellung zu dem, was
 *    unser System gerechnet hat"
 *
 * DIE RECHNUNG DAHINTER STEHT OHNE JSX DANEBEN (`teile/lebmengen.ts`). Ob
 * eine getippte Zahl von der gerechneten abweicht, ob eine Zeile ihren Grund
 * hat und was am Ende gespeichert wird, lässt sich nur AUSRECHNEN - eine
 * Textsuche misst, dass etwas dasteht, nicht, dass es stimmt.
 */
function MengenDialog(
  { zeilen, zu, speichere, laeuft, fehler, hoechstens }: {
    zeilen: Anzeigezeile[]; zu: () => void; laeuft: boolean;
    fehler: Fehlerlage | null;
    /** Die Hoechstlaenge des Vermerks, wie der Server sie nennt. */
    hoechstens: Hoechstlaenge;
    speichere: (liste: Aenderung[]) => Promise<void>;
  }
) {
  const [e, setE] = useState<Record<string, Eingabe>>(() => eingabenAus(zeilen));
  const v = vorhabenAus(zeilen, e, hoechstens);
  const offen = nochNichtGespeichert(zeilen, e);

  function setz(id: string, teil: Partial<Eingabe>) {
    setE((alt) => ({ ...alt, [id]: { ...alt[id], ...teil } }));
  }

  /* EIN FENSTER, DAS ZWEI DINGE SETZT, NENNT BEIDE.
     =====================================================================
     Es hiess „Mengen für diesen Export", und seit dem 14.09.2026 setzt es
     auch den Vermerk. Ein Titel, der die Hälfte dessen nennt, was darin
     steht, ist beim 20. Mal die Ursache dafür, dass jemand die andere
     Hälfte woanders sucht. */
  return (
    <Dialog titel="Mengen und Vermerke für diesen Export"
            laeuft={laeuft} zu={zu} breite={1040}>
      <Fehlerkasten lage={fehler} />

      <div className="hinweis">
        Hier steht, was <b>abgerechnet</b> wird. Das Aufmaß bleibt
        unangetastet: dort steht, was der Partner gemeldet und was der
        Innendienst geprüft hat. Wer eine Menge oder einen Vermerk auf den
        gerechneten Wert zurücksetzt, nimmt seine Änderung zurück. <b>0</b>
        heißt: diese Position geht nicht mit.
      </div>

      <table className="tabelle" style={{ marginTop: 10 }}>
        <thead>
          <tr>
            <th style={{ width: 100 }}>OZ</th>
            {/* DIE LEISTUNG STEHT NEBEN IHRER NUMMER, HIER WIE NEBENAN.
                ============================================================
                ORIGINALTEXT MARVIN (14.09.2026, Bild 6)
                  „hier fehlen die Leistungsbezeichnungen"

                Die Aufstellung auf dem Bildschirm dahinter trägt sie seit
                dem 10.09.2026; dieses Fenster zeigt DIESELBEN Zeilen und
                liess sie weg. **Ein Fenster, in dem man die Mengen ändert,
                ist der teurere Ort dafür:** dort steht die Zahl, die
                hinausgeht, und wer sie an der falschen Zeile ändert, merkt
                es erst bei der Telekom.

                UND SIE BEKOMMT EINE BREITE. Ohne eine trüge sie als einzige
                Spalte den ganzen übrigen Platz und schöbe Vermerk und Grund
                an den rechten Rand - derselbe Fehler, den dieselbe Spalte
                nebenan am 10.09.2026 schon einmal gemacht hat. */}
            <th style={{ width: '26%' }}>Leistung laut Rahmenvertrag</th>
            <th style={{ width: 80, textAlign: 'right' }}>Gemeldet</th>
            <th style={{ width: 80, textAlign: 'right' }}>Geprüft</th>
            <th style={{ width: 110 }}>Abgerechnet</th>
            {/* DER VERMERK GEHT IN DIE DATEI, DER GRUND BLEIBT IM HAUS. Zwei
                Spalten, zwei Fragen - in einer wären sie eine Spalte, die
                zwei Dinge beantwortet, und dann verliert sie eines davon. */}
            <th style={{ width: '30%' }}>Vermerk (geht in die Datei)</th>
            <th>Grund der Abweichung (bleibt im Haus)</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z) => {
            const id = z.aufmassPositionId ?? '';
            const eing = e[id] ?? { menge: '', grund: '', vermerk: '' };
            /* DER GRUND IST PFLICHT AN DER MENGE UND NICHT AN DER ZEILE. Eine
               Zeile, die NUR den Vermerk setzt, braucht keinen - er geht in
               die Datei und trägt seine Begründung selbst. Gefragt wird
               deshalb die getippte MENGE und nicht, ob diese Zeile
               irgendetwas ändert. */
            const mengeWeicht = mengeAusFeld(eing.menge) !== z.mengeGerechnet;
            return (
              <tr key={z.aufmassPositionId ?? z.oz}>
                <td className="einfarbig">{z.oz}</td>
                {/* EINE LEERE ZELLE IST VON EINER FEHLENDEN ANGABE NICHT ZU
                    UNTERSCHEIDEN. Eine Position, die der Leistungskatalog
                    nicht kennt, trägt keinen Text; das ist eine Auskunft und
                    kein Anzeigefehler, und sie wird hingeschrieben. */}
                <td>{z.text || <span className="leise">nicht im LV</span>}</td>
                <td style={{ textAlign: 'right' }} className="leise">
                  {z.mengeSub === null ? '–' : menge(z.mengeSub)}
                </td>
                <td style={{ textAlign: 'right' }} className="leise">
                  {z.mengeMds === null ? '–' : menge(z.mengeMds)}
                </td>
                <td>
                  <input
                    className={v.unlesbar.includes(z.oz) ? 'falsch' : undefined}
                    style={{ width: 90, textAlign: 'right' }}
                    value={eing.menge} disabled={!id || laeuft}
                    onChange={(x) => setz(id, { menge: x.target.value })} />
                  <div className="klein leise">
                    gerechnet {feldAusMenge(z.mengeGerechnet)}
                  </div>
                </td>
                <td>
                  <input
                    className={v.zuLang.includes(z.oz) ? 'falsch' : undefined}
                    style={{ width: '100%' }}
                    maxLength={hoechstens ?? undefined}
                    value={eing.vermerk} disabled={!id || laeuft}
                    onChange={(x) => setz(id, { vermerk: x.target.value })} />
                  <div className="klein leise">
                    gerechnet {z.sectionTextGerechnet || '(kein Abschnitt)'}
                  </div>
                </td>
                <td>
                  <input
                    className={v.ohneGrund.includes(z.oz) ? 'falsch' : undefined}
                    style={{ width: '100%' }}
                    value={eing.grund} disabled={!id || laeuft}
                    placeholder={mengeWeicht
                      ? 'Pflicht, sobald die Menge abweicht' : ''}
                    onChange={(x) => setz(id, { grund: x.target.value })} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* EIN GESPERRTER KNOPF OHNE GRUND SIEHT AUS WIE EIN KAPUTTER. */}
      {v.unlesbar.length > 0 && (
        <div className="hinweis fehler" style={{ marginTop: 10 }}>
          Diese Menge lässt sich nicht lesen: {v.unlesbar.join(', ')}.
          Dezimalkomma, keine Einheit.
        </div>
      )}
      {v.ohneGrund.length > 0 && (
        <div className="hinweis fehler" style={{ marginTop: 10 }}>
          Es fehlt der Grund bei: {v.ohneGrund.join(', ')}. Wer eine Menge
          anders abrechnet, als das System sie gerechnet hat, schreibt dazu,
          warum - sonst ist die Abrechnung später nicht zu verteidigen.
        </div>
      )}
      {/* UNSERE GRENZE WIRD ALS UNSERE BENANNT. Was das Portal an dieser
          Stelle wirklich annimmt, steht in einer Schreibvorschrift, die hier
          nicht vorliegt; eine Zahl, die wir uns ausdenken und der Telekom
          zuschreiben, schickt beim nächsten abgewiesenen Hochladen niemanden
          mehr an die richtige Stelle. */}
      {v.zuLang.length > 0 && (
        <div className="hinweis fehler" style={{ marginTop: 10 }}>
          Der Vermerk ist zu lang bei: {v.zuLang.join(', ')}. Die Vergabe
          nimmt {hoechstens} Zeichen.
        </div>
      )}

      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt"
                disabled={laeuft || !!v.unlesbar.length || !!v.ohneGrund.length
                                 || !!v.zuLang.length || !offen}
                onClick={() => speichere(v.aenderungen)}>
          {laeuft ? 'Einen Moment' : 'Speichern'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
        {!offen && !v.unlesbar.length && !v.ohneGrund.length
          && !v.zuLang.length && (
          <span className="klein leise">Es steht schon so da.</span>
        )}
      </div>
    </Dialog>
  );
}


function ErzeugenDialog({ d, zu, erzeuge, laeuft, fehler }: any) {
  const [art, setArt] = useState(d.vorschlag.art);
  const [schluss, setSchluss] = useState(d.vorschlag.finalMark);

  // Ein Teilaufmass geht ohne Schlussmarke hinaus, alles andere mit. Die
  // Datenbank besteht darauf, deshalb folgt der Haken der Art.
  useEffect(() => { setSchluss(art !== 'teil'); }, [art]);

  return (
    <Dialog titel="LEB-Datei erzeugen" laeuft={laeuft} zu={zu} breite={640}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise">
        {d.offen.length} Positionen, Leistungszeitraum {datum(d.zeitraum.von)}
        {' '}bis {datum(d.zeitraum.bis)}, Beleg {d.belegnummer}.
      </p>

      <Auswahl name="art" titel="Art" pflicht wert={art} setze={setArt}
               leerText="" breite="100%" fehler={fehler} optionen={ARTEN}
               hinweis={d.vorschlag.grund} />

      <Haken name="final_mark" titel="Schlussmarke setzen (finalMark = yes)"
             wert={schluss} setze={setSchluss} gesperrt
             hinweis="Folgt der Art: ein Teilaufmaß geht ohne, alles andere mit.
                      Beim Einblasstop erst das Teilaufmaß, später der Nachtrag
                      als Schlussaufmaß. Ein LEB ist eine Datei." />

      <div className="hinweis" style={{ marginTop: 10 }}>
        Die Datei landet unter <b>Dateien, Ordner „Abrechnung/LEB"</b> und ist
        für Subunternehmer nicht sichtbar: darin stehen unsere Mengen.
        Hochgeladen wird sie von Hand im Portal.
      </div>

      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft}
                onClick={() => erzeuge({ art, final_mark: schluss })}>
          {laeuft ? 'Einen Moment' : 'Erzeugen'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}
